import { UseQueryResult, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { useUpdateCompanyStates } from '../hooks/useUpdateCompanyAndState';
import {
  fetchRespondentApprovalStatuses,
  suggestRespondents,
  withdrawRespondents,
} from '../queries/KpiCollectionQueries';
import {
  buildRespondentStatusLookup,
  RespondentStatusChange,
  RespondentStatusLookup,
} from '../../util/respondentStatus';

export const respondentApprovalStatusKeys = {
  all: ['respondent-approval-statuses'] as const,
};

export const respondentApprovalStatusesQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: respondentApprovalStatusKeys.all,
    queryFn: fetchRespondentApprovalStatuses,
    // Stable module-level `select` ref: react-query memoizes the derived lookup, so the grid's
    // colDefs don't rebuild every render. Don't inline this as an arrow.
    select: buildRespondentStatusLookup,
    enabled,
  });

export function useRespondentApprovalStatuses(enabled = true): UseQueryResult<RespondentStatusLookup, Error> {
  return useQuery(respondentApprovalStatusesQueryOptions(enabled));
}

// Persist respondent suggest/withdraw for an onboarded company via the dedicated Starbreaker
// endpoints (proxied by Maggie), then refetch statuses. Both write synchronously and are
// authoritative — no optimistic overlay or polling.
export function useApplyRespondentSuggestions(): (
  companyId: number,
  change: RespondentStatusChange
) => Promise<void> {
  const queryClient = useQueryClient();
  const { pushWarningToast } = useToastMessageState();
  const refreshCompanyState = useUpdateCompanyStates();
  return useCallback(
    async (companyId, change) => {
      if (change.added.length === 0 && change.removed.length === 0) {
        return;
      }
      // No catch — the error propagates to the caller, which toasts it and keeps the modal open.
      try {
        // Withdraw before suggest: both read-modify-write the company's respondent list, so sequencing
        // avoids a lost update (added/removed are disjoint, so order can't drop an email).
        if (change.removed.length > 0) {
          await withdrawRespondents(companyId, change.removed);
        }
        if (change.added.length > 0) {
          const { results } = await suggestRespondents(companyId, change.added);
          const blocked = results.filter((r) => r.outcome === 'blocked_rejected').map((r) => r.email);
          if (blocked.length > 0) {
            pushWarningToast({
              message: `The company has denied access to ${blocked.join(', ')} — please contact them directly. Not added.`,
            });
          }
        }
      } finally {
        // Resync in `finally` so a partial success (e.g. withdraw landed, then suggest threw) still
        // refreshes every view. Both are best-effort/fire-and-forget: the SB status list drives the
        // respondent UI; companyStateJ isn't read by this form but is kept in sync for other views
        // that read company.respondents.
        void refreshCompanyState(companyId);
        void queryClient.invalidateQueries({ queryKey: respondentApprovalStatusKeys.all });
      }
    },
    [queryClient, pushWarningToast, refreshCompanyState]
  );
}
